import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Company,
  CompanySearchOrderBy,
  CompaniesSearchOrderField,
  OrderDirection,
  SearchCompaniesListDocument,
  SearchCompaniesListQuery,
  SearchCompaniesListQueryVariables,
  LocaleEnum,
  Metadata,
} from 'generated/apolloComponents'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import CompaniesFilters from '@/components/organisms/CompaniesFilters'
import { arrayToObject, isEmpty } from '@/helpers/utils'
import {
  ActionType,
  CompaniesContext,
  CompaniesStateFilters,
  convertProfilesFiltersSlugToURL,
  convertProfilesStateFiltersToQueryFilters,
  OrderStateType,
} from '@/context/companiesContext'
import { initializeApollo } from '@/api/apollo'
import Pagination from '../molecules/Pagination'
import MultiSelect, { SelectDataType } from '../atoms/Select/Multi'
import { sorting, sortingCompanies } from '@/data/sortingOptions'
import FiltersList from '../molecules/FiltersList'
import CompanyListItem from '../molecules/CompanyListItem'
import { companyListResultLabel } from '@/helpers/profileUtils'

interface IProps {
  locations: string[]
  companies: Company[]
  totalCount: number
  slugState: any
  metaData: Metadata
}

const CompanyProfilesTemplate = ({
  locations,
  companies,
  totalCount,
  slugState,
  metaData,
}: IProps) => {
  const { state, dispatch } = useContext(CompaniesContext)
  const { t, lang } = useTranslation('common')
  const [companiesList, setCompaniesList] = useState(companies)
  const [companiesCount, setCompaniesCount] = useState(totalCount)
  const router = useRouter()
  const isMounted = useRef(false)

  const orderWithLabel = (order: OrderStateType) => {
    if (!order.value) return null

    const labelName = sorting.find(
      (item) => item.value === `${order.value.field},${order.value.direction}`
    )?.label

    return {
      label: t(`common:${labelName}`),
      value: order.value,
    }
  }

  useEffect(() => {
    if (slugState && !isEmpty(slugState)) {
      dispatch({ type: ActionType.SetState, payload: slugState })
    }
  }, [])

  useEffect(() => {
    if (router.asPath === '/companies/profiles') {
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { city: [], company: '', skills: [], topTech: false },
      })
    }
  }, [router.asPath])

  useEffect(() => {
    const slug = convertProfilesFiltersSlugToURL(state)

    const loadData = async function () {
      const apolloClient = initializeApollo({})
      if (slug) {
        router.push({
          pathname: `/companies/profiles${slug}`,
        })
      } else {
        router.push({
          pathname: `/companies/profiles${slug}`,
        })
      }

      const queryByState = convertProfilesStateFiltersToQueryFilters(
        state.filters as CompaniesStateFilters
      )

      const variables: any = {
        page: state.page,
        perPage: state.perPage,
        language: LocaleEnum[lang as 'pl' | 'en'],
        filters: queryByState,
      }
      if (
        state.order &&
        state.order.value &&
        (state.order.value as CompanySearchOrderBy).field !== 'DEFAULT'
      )
        variables.order = state.order.value as CompanySearchOrderBy

      const companiesData = await apolloClient.query<
        SearchCompaniesListQuery,
        SearchCompaniesListQueryVariables
      >({
        query: SearchCompaniesListDocument,
        variables: variables,
      })

      setCompaniesList(companiesData.data.searchCompanies.nodes as Company[])
      setCompaniesCount(companiesData.data.searchCompanies.totalCount)
    }
    isMounted.current ? loadData() : (isMounted.current = true)
  }, [state])

  const handlePageChange = (page: number) => {
    dispatch({ type: ActionType.ChangePage, payload: page })
  }

  const handleOrderChange = (value: SelectDataType | null, key: string) => {
    if (value) {
      let sorting = null
      if (typeof value.value === 'string') {
        sorting = value.value.split(',')
        sorting = {
          direction: sorting[1] as OrderDirection,
          field: sorting[0] as CompaniesSearchOrderField,
        }
      }
      let tmp: OrderStateType = {
        label: value.label,
        value: sorting as CompanySearchOrderBy,
      }
      if (value.value === 'default') {
        // @ts-ignore
        tmp = undefined
      }
      dispatch({ type: ActionType.ChangeOrder, payload: tmp })
    }
  }

  return (
    <div className='bg-white md:bg-gray-bg pt-10'>
      <div className='container mb-4'>
        <h1 className='font-bold text-5xl'>{metaData.title}</h1>
      </div>
      <p className='container text-18 mt-14 mb-8 md:hidden'>
        {t('companies:companies_desc_mobile')}
      </p>
      <CompaniesFilters
        locations={arrayToObject(locations)}
        slugState={slugState}
        handleOrderChange={handleOrderChange}
        totalCount={totalCount}
      />

      <div className='py-8 relative'>
        <div className='container'>
          <div className='mb-10'>
            <div className='flex'>
              <div className='mt-2'>
                <div className='flex mt-6'>
                  <p className='text-18 font-medium mr-10 whitespace-nowrap'>
                    <span className='text-black'>
                      {companyListResultLabel(companiesCount)}
                    </span>
                  </p>

                  <FiltersList state={state} dispatch={dispatch} />
                </div>
              </div>

              <div className='ml-auto mt-auto'>
                <MultiSelect
                  state={state}
                  type='single'
                  value={
                    (state.order && orderWithLabel(state.order)) || {
                      label: t('common:default'),
                      value: { direction: undefined, field: 'DEFAULT' },
                    }
                  }
                  name='order'
                  onSelect={handleOrderChange}
                  data={sortingCompanies.map(
                    (item) =>
                      (item = { ...item, label: t(`common:${item.label}`) })
                  )}
                  title={t('common:sort')}
                  className='flex-1 w-96 multiSelect--small hidden md:block'
                />
              </div>
            </div>
          </div>
          {companiesList.map((company, index) => (
            <CompanyListItem index={index} company={company} />
          ))}
        </div>

        <Pagination
          className='pagination-bar'
          currentPage={state.page}
          totalCount={companiesCount}
          pageSize={state.perPage}
          onPageChange={(page: number) => handlePageChange(page)}
        />
      </div>
    </div>
  )
}

export default CompanyProfilesTemplate
